import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { endpoints } from "../../../services/endpoint";
import { axiosInstance } from "../../../services/axiosInstance";
import { synthesizeSpeech, fetchPollyVoices } from "../../../services/pollyService";
import WavEncoder from "wav-encoder";
import CloseIcon from "@mui/icons-material/Close";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import "./editprompt.css";

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });


const EditPromptsPage = ({
  prompt,
  onClose,
  onUpdate,

}) => {
  const [name, setName] = useState(prompt.name || "");
  const [description, setDescription] = useState(prompt.description || "");
  const instanceAlias = localStorage.getItem("instanceName");
  const email = localStorage.getItem("Email");
  const username = localStorage.getItem("username");
  const instanceId = localStorage.getItem("InstanceId");
  const EncryptedInstanceId = localStorage.getItem("EncryptedId");
  const customerAWSAccountId = localStorage.getItem("CustomerAWSAccountId");
  const region = localStorage.getItem("Region");
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [selectedOption, setSelectedOption] = useState("upload");
  const [file, setFile] = useState(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationRef = useRef(null);
  const [actionClicked, setActionClicked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(audioUrl);

  // Text-to-audio states
  const [text, setText] = useState("");
  const [audioBase64Edit, setAudioBase64Edit] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptError, setTranscriptError] = useState(false);
  const [isTextInitialized, setIsTextInitialized] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState("Joanna");
  const [pollyError, setPollyError] = useState("");
  const [pollyAudioUrl, setPollyAudioUrl] = useState(null);
  const justSavedAudioRef = useRef(false);
  const [pollyVoices, setPollyVoices] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchPollyVoices().then(setPollyVoices);
  }, []);

  const fetchPromptContext = async (promptName) => {
    try {
      const payload = {
        action: "ListContext",
        InstanceAlias: instanceAlias,
        ID: EncryptedInstanceId,
        region,
        awsAccount: customerAWSAccountId,
      };

      const response = await axiosInstance.post(
        "https://grj87qmm19.execute-api.us-east-1.amazonaws.com/Test/ListPromptContext",
        payload
      );

      // 🔥 IMPORTANT: handle body parsing properly
      const parsed = typeof response.data.body === "string"
        ? JSON.parse(response.data.body)
        : response.data;

      const contextList = parsed?.data || [];

      const matched = contextList.find(
        (item) => item.PromptName === promptName
      );

      if (matched && !isTextInitialized) {
        if (matched.ContextText) {
          setText(matched.ContextText);
          setSelectedOption("polly"); // Set to polly if context exists
        } else {
          setSelectedOption("upload");
        }

        // ✅ NEW: restore saved voice
        if (matched.Voice) {
          setSelectedVoice(matched.Voice);
        }

        setIsTextInitialized(true);
      }
    } catch (error) {
      console.error("Error fetching context:", error);
    }
  };

  // ✅ Reset when a new prompt is opened
  useEffect(() => {
    setIsTextInitialized(false);
    setText("");
    setSelectedVoice("Joanna");
  }, [prompt]);

  useEffect(() => {
    if (prompt?.name) {
      fetchPromptContext(prompt.name);
    }
  }, [prompt]);


  useEffect(() => {
    if (!audioUrl && prompt?.name && !justSavedAudioRef.current) {
      fetchAudioByName(prompt.name);
    }
  }, [prompt?.name, audioUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAudioByName = async (promptName) => {
    setLoading(true);
    try {
      const payload = {
        action: "play",
        promptName,
        InstanceAlias: instanceAlias,
        ID: EncryptedInstanceId,
        instanceAlias,
        region: region,
        awsAccount: customerAWSAccountId,
      };

      const response = await fetch(endpoints.promptsDisplay.promptlist, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      const fetchedAudioUrl = JSON.parse(data.body).promptFile.PromptPresignedUrl;

      if (fetchedAudioUrl) {
        setCurrentAudioUrl(fetchedAudioUrl);
      }
    } catch (error) {
      console.error("Error fetching the audio file:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTranscript = async (promptName) => {
    if (isTextInitialized) return; // 🚫 prevent override

    setIsTranscribing(true);
    setTranscriptError(false);

    try {
      const payload = {
        action: "transcribe",
        promptName,
        InstanceAlias: instanceAlias,
        ID: EncryptedInstanceId,
        region,
        awsAccount: customerAWSAccountId,
      };

      const response = await axiosInstance.post(
        endpoints.promptsDisplay.promptlist,
        payload
      );

      const transcript = JSON.parse(response.data.body)?.transcript;

      // ✅ Only set if still empty
      if (transcript && !isTextInitialized) {
        setText(transcript);
        setIsTextInitialized(true);
      } else {
        setTranscriptError(true);
      }
    } catch (error) {
      console.error("Error fetching transcript:", error);
      setTranscriptError(true);
    } finally {
      setIsTranscribing(false);
    }
  };


  const visualizeFrequency = () => {
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);

    const boxes = document.querySelectorAll(".frequency-box");
    const step = Math.ceil(dataArrayRef.current.length / boxes.length);

    boxes.forEach((box, i) => {
      const value = dataArrayRef.current[i * step];
      box.style.height = `${value / 2}px`;
      box.style.backgroundColor = `rgb(${value + 100}, 50, 50)`;
    });

    animationRef.current = requestAnimationFrame(visualizeFrequency);
  };


  useEffect(() => {
    const fetchAudioUrl = async () => {
      // Skip re-fetch if we just saved new audio — currentAudioUrl is already correct
      if (justSavedAudioRef.current) {
        // Reset after a tick so both useEffects see the flag
        setTimeout(() => { justSavedAudioRef.current = false; }, 500);
        return;
      }
      try {
        const payload = {
          action: "play",
          InstanceAlias: instanceAlias,
          ID: prompt.Id,
          awsAccount: customerAWSAccountId,
          region,
          promptName: prompt.name,
        };

        const response = await axiosInstance.post(
          endpoints.promptsDisplay.promptlist,
          payload
        );

        const url = JSON.parse(response.data.body)?.promptFile?.PromptPresignedUrl;
        if (url) {
          setAudioUrl(url);
          fetchTranscript(prompt.name);
        }
      } catch (error) {
        console.error("Error fetching prompt audio URL:", error);
      }
    };

    fetchAudioUrl();
  }, [prompt, instanceAlias, customerAWSAccountId, region]); // eslint-disable-line react-hooks/exhaustive-deps

  const [namesFileEdit, setNamesFileEdit] = useState([]);

  const fetchPrompts = async () => {
    const body = {
      action: "list",
      ID: EncryptedInstanceId,
      region,
      awsAccount: customerAWSAccountId,
      InstanceAlias: instanceAlias,
    };

    try {
      const response = await axiosInstance.post(endpoints.promptsDisplay.promptlist, body);
      if (response.data.statusCode === 200) {
        const parsed = JSON.parse(response.data.body);
        const promptList = parsed.prompts;

        if (Array.isArray(promptList)) {
          return promptList.map((item) => ({
            name: item.Name || "Unnamed Prompt",
            id: item.Id || "-",
            arn: item.ARN || "-",
            description: item.Description || "No description",
            tags: item.Tags && Object.keys(item.Tags).length > 0 ? item.Tags : {},
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching prompts list:", error);
    }

    return [];
  };

  const handlePlayText = async () => {
    if (!text.trim()) return;

    setIsPlaying(true);
    setPollyError("");

    try {
      const base64Audio = await synthesizeSpeech(text, selectedVoice);
      setAudioBase64Edit(base64Audio);

      // Create a playable URL for preview
      const byteChars = atob(base64Audio);
      const byteNumbers = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([byteNumbers], { type: "audio/wav" });
      const url = URL.createObjectURL(blob);
      setPollyAudioUrl(url);
      
      // Play the audio automatically
      const audioToPlay = new Audio(url);
      audioToPlay.play();
      
    } catch (err) {
      console.error("Polly synthesis error:", err);
      setPollyError(
        "Failed to generate audio. Please check your AWS Polly configuration."
      );
    } finally {
      setIsPlaying(false);
    }
  };

  const [cancelButtonLoading, setCancelButtonLoading] = useState(false);

  const handleCancel = () => {
    setActionClicked("cancel");
    setCancelButtonLoading(true);
    setTimeout(() => {
      setCancelButtonLoading(false);
      setActionClicked(null);
      onClose();
    }, 500);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().endsWith(".wav")) {
        setErrors((prev) => ({ ...prev, audio: "Please select a valid .wav file." }));
        setFile(null);
        return;
      }
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.audio;
        return newErrors;
      });
      setFile(selectedFile);
    }
  };

  const handleSave = async () => {
    setActionClicked("save");
    setSaveLoading(true);

    let audioBase64 = null;

    // ------------------ VALIDATION ------------------
    const newErrors = {};

    const oldPromptName = prompt.name;
    const oldPromptDescription = prompt.description;

    const isNameChanged = name !== oldPromptName;
    const isDescriptionChanged = description !== oldPromptDescription;

    if (!name.trim()) {
      newErrors.name = "Name is required";
    } else if (!isNameChanged) {
      newErrors.name = "Please change the prompt name before saving";
    }

    if (!description.trim()) {
      newErrors.description = "Description is required";
    }

    if (selectedOption === "polly") {
      if (!text.trim()) {
        newErrors.context = "Context is required when using Polly";
      }
    }

    // ❌ STOP if validation fails
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaveLoading(false);
      setActionClicked(null);
      return;
    }

    setErrors({}); // clear errors if valid

    // ------------------ AUDIO HANDLING ------------------
    if (selectedOption === "polly") {
      try {
        audioBase64 = await synthesizeSpeech(text, selectedVoice);
      } catch (err) {
        console.error("Polly synthesis error on save:", err);
        setSaveLoading(false);
        setActionClicked(null);
        return;
      }
    } else if (selectedOption === "upload" && file) {
      try {
        audioBase64 = await toBase64(file);
      } catch (error) {
        console.error("Error converting uploaded file to Base64", error);
        setSaveLoading(false);
        setActionClicked(null);
        return;
      }
    }

    const isFileChanged = audioBase64 !== null;

    // ------------------ PAYLOAD ------------------
    const updatedData = {
      awsAccount: customerAWSAccountId,
      ID: EncryptedInstanceId,
      region,
      InstanceAlias: instanceAlias,
      UserName: username,
      Email: email,
      action: "update",
      OldPromptName: oldPromptName,
      NewPromptName: name,
      BucketName: `${customerAWSAccountId}-${instanceAlias}-prompts`,
    };

    if (isDescriptionChanged) {
      updatedData.NewPromptDescription = description;
    }

    if (audioBase64) {
      updatedData.object_key = name; // since name must change
      updatedData.file_content = audioBase64;
    }

    try {
      const response = await axiosInstance.post(
        endpoints.promptsDisplay.editPrompt,
        updatedData
      );

      console.log("Prompt updated successfully", response.data);

      // ------------------ CONTEXT + VOICE UPDATE ------------------
      try {
        const contextPayload = {
          action: "UpdateContext",
          PromptName: name,
          ContextText: selectedOption === "polly" ? text : "",
          Voice: selectedVoice,
        };

        await axiosInstance.post(
          "https://grj87qmm19.execute-api.us-east-1.amazonaws.com/Test/UpdatePromptContext",
          contextPayload
        );

        console.log("Context updated successfully");
      } catch (contextError) {
        console.error("Error updating context:", contextError);
      }

      // ------------------ AUDIO REFRESH ------------------
      if (isFileChanged) {
        justSavedAudioRef.current = true;

        if (pollyAudioUrl) {
          setCurrentAudioUrl(pollyAudioUrl);
        } else {
          await new Promise((r) => setTimeout(r, 3000));
          await fetchAudioByName(name);
        }
      }

      // ------------------ REFRESH LIST ------------------
      const updatedList = await fetchPrompts();
      onUpdate(updatedList, name);

    } catch (error) {
      console.error("Error updating prompt:", error);
    } finally {
      setSaveLoading(false);
      setActionClicked(null);
    }
  };

  return (
    <Container className="editprompt-content-container">
      <Box display="flex" flexDirection="column" alignItems="flex-start" mb={2} gap={1}>
        <IconButton
          onClick={onClose}
          size="medium"
          sx={{
            border: "1px solid #ccc",
            borderRadius: "50%",
            padding: 1,
            bgcolor: "#f5f5f5",
            "&:hover": { bgcolor: "#e0e0e0" }
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h4" className="phone-text">
          Edit Prompt - {prompt.name}
        </Typography>
      </Box>

      <Box className="editprompt-form-section">
        <div className="queue-form-fields">
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            placeholder="Enter Name"
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            placeholder="Enter description"
            error={!!errors.description}
            helperText={errors.description}
          />
        </div>
        <br />
        <Typography className="editprompt-headings">
          Current file
        </Typography>
        <div>
          {loading ? (
            <CircularProgress />
          ) : currentAudioUrl ? (
            <div>
              <audio controls className="audifile-css" key={currentAudioUrl} src={currentAudioUrl} />
            </div>
          ) : (
            <Typography>No audio available for this prompt.</Typography>
          )}
        </div>

        <br />
        <Box sx={{ mt: 3, mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 4 }}>
          <Typography className="editprompt-headings" sx={{ mb: 0, mt: 0 }}>
            Update Audio Mode
          </Typography>
          <div className="radio-group" style={{ margin: 0, display: "flex", gap: "20px" }}>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", margin: 0 }}>
              <input
                type="radio"
                value="polly"
                checked={selectedOption === "polly"}
                onChange={(e) => {
                  setSelectedOption(e.target.value);
                  setErrors({});
                }}
                style={{ marginRight: "6px" }}
              />
              Generate with Amazon Polly
            </label>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", margin: 0 }}>
              <input
                type="radio"
                value="upload"
                checked={selectedOption === "upload"}
                onChange={(e) => {
                  setSelectedOption(e.target.value);
                  setErrors({});
                }}
                style={{ marginRight: "6px" }}
              />
              Upload .WAV File
            </label>
          </div>
        </Box>

        <div className="space-y-4" style={{ width: "100%" }}>
          {selectedOption === "polly" && (
             <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', backgroundColor: "#f9fafb", borderRadius: "8px", padding: "16px" }}>
              <TextField
                label="Context"
                value={text}
                onChange={(e) => setText(e.target.value)}
                error={!!errors.context}
                helperText={errors.context}
                placeholder={isTranscribing ? "Reading audio..." : "Audio text will appear here..."}
                sx={{ minWidth: 200, maxWidth: 300, flex: 1 }}
                slotProps={{
                  input: {
                    endAdornment: isTranscribing ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />

              <FormControl sx={{ minWidth: 200, maxWidth: 300, flex: 1 }}>
                <InputLabel id="polly-voice-label-edit">Polly Voice</InputLabel>
                <Select
                  labelId="polly-voice-label"
                  value={selectedVoice || ""}
                  label="Polly Voice"
                  onChange={(e) => setSelectedVoice(e.target.value)}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {pollyVoices.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name} - {v.language}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  onClick={handlePlayText}
                  disabled={!text.trim() || isPlaying}
                  color="primary"
                  sx={{ backgroundColor: "#e3f2fd", '&:hover': { backgroundColor: "#bbdefb" }, p: 1.5 }}
                >
                  {isPlaying ? <CircularProgress size={24} /> : <VolumeUpIcon />}
                </IconButton>
              </Box>
            </Box>
          )}

          {selectedOption === "upload" && (
            <div className="upload-section" style={{ backgroundColor: "#f9fafb", borderRadius: "8px", padding: "16px" }}>
              {errors.audio && <p className="error-message" style={{ color: "red", marginTop: 0 }}>{errors.audio}</p>}
              <Typography variant="body2" sx={{ mb: 2 }}>
                Choose a <b>.wav</b> file to save as a prompt over the existing audio.
              </Typography>
              <input
                type="file"
                accept=".wav"
                onChange={handleFileChange}
                style={{ display: "block", marginBottom: "10px" }}
              />
              {file && <Typography variant="body2" color="primary">Selected file: {file.name}</Typography>}
            </div>
          )}
        </div>

        {pollyError && selectedOption === "polly" && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {pollyError}
          </Typography>
        )}
        {transcriptError && selectedOption === "polly" && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Could not read audio text automatically. You can type the text manually above.
          </Typography>
        )}
        {/* {audioBase64Edit && selectedOption === "polly" && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1.5 }}>
            Audio preview generated successfully (Voice: {selectedVoice}) — will be saved on Save.
          </Typography>
        )} */}
      </Box>

      <div className="buttons-addhour">
        <Button
          onClick={handleCancel}
          disabled={actionClicked === "save" || cancelButtonLoading}
        >
          <span className="adduser-button-content">
            {actionClicked === "cancel" ? (
              <CircularProgress size={18} style={{ color: "red" }} />
            ) : (
              "Cancel"
            )}
          </span>
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={actionClicked === "cancel" || saveLoading}
        >
          <span className="adduser-button-content">
            {actionClicked === "save" ? (
              <CircularProgress size={18} style={{ color: "white" }} />
            ) : (
              "Save"
            )}
          </span>
        </Button>
      </div>
    </Container>
  );
};

export default EditPromptsPage;
